import React, { useEffect, useMemo, useState } from "react";
import { HiTrophy } from "react-icons/hi2";
import { PeakEvent, eventIncludesDay } from "../../types/event";
import { useUserEvents } from "../../hooks/useUserEvents";
import { getLoggedEventIds } from "../../services/eventService";
import PerformanceIntentBadge from "../shared/PerformanceIntentBadge";
import { formatDateRange, todayYYYYMMDD } from "../../utils/goalDates";

interface EventsToLogSectionProps {
  userId: string | undefined;
  /** The viewed day, YYYYMMDD. */
  dateID: string;
  /** Bump to refetch after a performance is saved. */
  refreshKey?: number;
  onLogEvent: (event: PeakEvent) => void;
}

/**
 * Dashboard nudge (Events.md §5): non-withdrawn events whose range includes
 * the viewed day (day <= today) with no linked training log. Same visual
 * pattern as "Sessions to log"; each row opens the Performance flow seeded
 * from the event.
 */
const EventsToLogSection: React.FC<EventsToLogSectionProps> = ({
  userId,
  dateID,
  refreshKey = 0,
  onLogEvent,
}) => {
  const { events } = useUserEvents(userId);
  const [loggedIds, setLoggedIds] = useState<Set<string>>(new Set());

  const onDay = useMemo(
    () =>
      dateID <= todayYYYYMMDD()
        ? events.filter(
            (e) => e.status !== "withdrawn" && eventIncludesDay(e, dateID),
          )
        : [],
    [events, dateID],
  );

  useEffect(() => {
    if (!userId || onDay.length === 0) {
      setLoggedIds(new Set());
      return;
    }
    let cancelled = false;
    getLoggedEventIds(userId, onDay).then((ids) => {
      if (!cancelled) setLoggedIds(ids);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, onDay, refreshKey]);

  const toLog = onDay.filter((e) => !loggedIds.has(e.id));
  if (toLog.length === 0) return null;

  return (
    <div className="bg-base-100 rounded-none md:rounded-lg px-8 py-4 mt-3">
      <h3 className="text-sm font-heading px-0 mb-1">
        Events <span className="font-grotesk text-base-content/60">to log</span>
      </h3>
      <p className="text-xs text-base-content/50 font-grotesk mb-4">
        Events on this day without a logged performance.
      </p>
      <div className="space-y-3">
        {toLog.map((event) => {
          return (
            <div
              key={event.id}
              onClick={() => onLogEvent(event)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onLogEvent(event);
                }
              }}
              className="p-4 rounded-xl border bg-base-200 border-base-300 hover:border-base-content/60 hover:bg-base-300 transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <HiTrophy className="w-4 h-4 flex-shrink-0 text-base-content/60" />
                    <div className="font-semibold text-base-content font-heading truncate">
                      {event.name || "Event"}
                    </div>
                    <PerformanceIntentBadge type={event.type} />
                  </div>
                  <div className="text-xs text-base-content/60 font-grotesk mt-0.5">
                    {formatDateRange(event.date, event.endDate)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLogEvent(event);
                  }}
                  className="btn btn-primary btn-sm min-h-11 h-11 rounded-xl px-3 text-xs font-medium font-grotesk normal-case flex-shrink-0"
                >
                  Log performance
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EventsToLogSection;
